import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { activitiesAPI, connectionsAPI } from '../api/client';
import Toast from '../components/Toast';
import { useActivityChat } from '../hooks/useActivityChat';
import { generateICS, getGoogleCalendarUrl } from '../utils/calendarExport';
import ShareButton from '../components/ShareButton';
import { Footprints, Coffee, Mountain, Bike, UtensilsCrossed, Landmark, MapPin, Send, Trash2 } from 'lucide-react';
import Layout from '../components/Layout';
import '../styles/activity-detail.css';

const ACTIVITY_ICONS = {
  run: Footprints,
  coffee: Coffee,
  hike: Mountain,
  cycling: Bike,
  dinner: UtensilsCrossed,
  museum: Landmark,
  other: MapPin,
};

function ActivityIcon({ type, size = 22, color = '#52AAD8' }) {
  const Icon = ACTIVITY_ICONS[type] || MapPin;
  return <Icon size={size} color={color} strokeWidth={2} />;
}

export default function ActivityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [activity, setActivity] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const [inviteToast, setInviteToast] = useState(null);
  const [showInviteFriends, setShowInviteFriends] = useState(false);
  const [friends, setFriends] = useState([]);
  const [selectedFriendIds, setSelectedFriendIds] = useState([]);
  const [inviting, setInviting] = useState(false);
  const calRef = useRef(null);
  const messagesEndRef = useRef(null);

  const currentUserId = localStorage.getItem('userId');

  const isCreatorLoaded = activity?.creator_id === currentUserId;
  const myRsvpLoaded = activity?.rsvps?.find((r) => r.user_id === currentUserId);
  const canChatLoaded = isCreatorLoaded || ['im_in', 'maybe'].includes(myRsvpLoaded?.response_type);

  const { messages, sendMessage, deleteMessage, connected } = useActivityChat(canChatLoaded ? id : null);

  useEffect(() => {
    loadActivity();
    loadFriends();
  }, [id]);

  const loadFriends = async () => {
    try {
      const res = await connectionsAPI.getAll();
      const accepted = (res.data || []).filter(c => c.status === 'accepted');
      setFriends(accepted.map(c => ({
        id: c.user_id === currentUserId ? c.connected_user_id : c.user_id,
        name: c.display_name,
      })));
    } catch {}
  };

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (calRef.current && !calRef.current.contains(e.target)) setCalOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadActivity = async () => {
    try {
      const res = await activitiesAPI.getById(id);
      setActivity(res.data);
    } catch (err) {
      console.error('Failed to load activity:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRSVP = async (responseType) => {
    try {
      await activitiesAPI.rsvp(id, responseType);
      loadActivity();
    } catch (err) {
      console.error('RSVP failed:', err);
    }
  };

  const handleGenerateInvite = async () => {
    try {
      const res = await activitiesAPI.generateInviteLink(id);
      const inviteUrl = `${window.location.origin}/join/${res.data.token}`;
      if (navigator.share) {
        await navigator.share({ title: activity.title || activity.activity_type, url: inviteUrl });
      } else {
        await navigator.clipboard.writeText(inviteUrl);
        setInviteToast('Invite link copied!');
      }
    } catch (err) {
      if (err.name !== 'AbortError') setInviteToast('Failed to generate link.');
    }
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    sendMessage(newMessage.trim());
    setNewMessage('');
  };

  const handleInviteFriends = async () => {
    if (selectedFriendIds.length === 0) return;
    setInviting(true);
    try {
      await activitiesAPI.inviteFriends(id, selectedFriendIds);
      setInviteToast(`Invited ${selectedFriendIds.length} friend${selectedFriendIds.length > 1 ? 's' : ''}!`);
      setShowInviteFriends(false);
      setSelectedFriendIds([]);
    } catch {
      setInviteToast('Failed to send invites.');
    }
    setInviting(false);
  };

  const toggleFriend = (fid) => {
    setSelectedFriendIds(prev => prev.includes(fid) ? prev.filter(x => x !== fid) : [...prev, fid]);
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel this activity? This cannot be undone.')) return;
    setCancelling(true);
    try {
      await activitiesAPI.cancel(id);
      navigate('/feed');
    } catch (err) {
      console.error('Failed to cancel:', err);
      setCancelling(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Anytime';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return <Layout title="Plan Detail"><div className="detail-loading">Loading...</div></Layout>;
  if (!activity) return <Layout title="Plan Detail"><div className="detail-loading">Activity not found.</div></Layout>;

  const isCreator = activity.creator_id === currentUserId;
  const myRsvp = activity.rsvps?.find((r) => r.user_id === currentUserId);
  const going = activity.rsvps?.filter((r) => r.response_type === 'im_in') || [];
  const maybe = activity.rsvps?.filter((r) => r.response_type === 'maybe') || [];
  const interested = activity.rsvps?.filter((r) => r.response_type === 'cant_but_interested') || [];
  const canChat = isCreator || ['im_in', 'maybe'].includes(myRsvp?.response_type);

  return (
    <Layout title="Plan Detail">
      <div className="detail-two-col">

        {/* ── Left panel: activity info ── */}
        <div className="detail-left">
          {!!activity.is_cancelled && (
            <div className="cancelled-banner">This activity has been cancelled.</div>
          )}

          {/* Title + visibility */}
          <div className="detail-title-block">
            <div className="detail-title-row">
              <h1 className="detail-title">{activity.title || activity.activity_type}</h1>
              <span className={`visibility-badge ${activity.visibility_level}`}>
                {activity.visibility_level.replace('_', ' ')}
              </span>
            </div>
            {activity.location_name && (
              <div className="detail-location">
                <MapPin size={14} color="#888" strokeWidth={2} />
                <span>{activity.location_name}</span>
              </div>
            )}
            {activity.start_time && (
              <div className="detail-when">
                <ActivityIcon type={activity.activity_type} size={14} color="#888" />
                <span>{formatDate(activity.start_time)}{activity.duration_minutes ? ` · ${activity.duration_minutes} min` : ''}</span>
              </div>
            )}
          </div>

          {/* Description */}
          {activity.description && (
            <p className="detail-description">{activity.description}</p>
          )}

          {/* RSVP */}
          {!activity.is_cancelled && (
            <div className="detail-rsvp-row">
              <button
                className={`btn-rsvp btn-in ${myRsvp?.response_type === 'im_in' ? 'active' : ''}`}
                onClick={() => handleRSVP('im_in')}
              >{myRsvp?.response_type === 'im_in' ? "✓ I'm in" : "I'm in"}</button>
              <button
                className={`btn-rsvp btn-maybe ${myRsvp?.response_type === 'maybe' ? 'active' : ''}`}
                onClick={() => handleRSVP('maybe')}
              >{myRsvp?.response_type === 'maybe' ? '✓ Maybe' : 'Maybe'}</button>
              <button
                className={`btn-rsvp btn-interested ${myRsvp?.response_type === 'cant_but_interested' ? 'active' : ''}`}
                onClick={() => handleRSVP('cant_but_interested')}
              >{myRsvp?.response_type === 'cant_but_interested' ? "✓ Can't" : "Can't"}</button>
              {isCreator && <span className="creator-note">You created this activity.</span>}
            </div>
          )}

          {/* Attendees */}
          {(going.length > 0 || maybe.length > 0 || interested.length > 0) && (
            <div className="attendees-section">
              {going.length > 0 && (
                <div className="attendee-group">
                  <span className="attendee-label">Going ({going.length})</span>
                  <div className="attendee-list">
                    {going.map((r) => <span key={r.user_id} className="attendee-chip">{r.display_name || 'Someone'}</span>)}
                  </div>
                </div>
              )}
              {maybe.length > 0 && (
                <div className="attendee-group">
                  <span className="attendee-label">Maybe ({maybe.length})</span>
                  <div className="attendee-list">
                    {maybe.map((r) => <span key={r.user_id} className="attendee-chip chip-maybe">{r.display_name || 'Someone'}</span>)}
                  </div>
                </div>
              )}
              {interested.length > 0 && (
                <div className="attendee-group">
                  <span className="attendee-label">Can't ({interested.length})</span>
                  <div className="attendee-list">
                    {interested.map((r) => <span key={r.user_id} className="attendee-chip chip-interested">{r.display_name || 'Someone'}</span>)}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="detail-actions">
            <ShareButton
              url={`${window.location.origin}/activity/${id}`}
              title={activity.title || activity.activity_type}
              text={`Join me for ${activity.title || activity.activity_type} on Covo!`}
              label="Share"
            />
            {friends.length > 0 && (
              <button className="btn-tertiary btn-small" onClick={() => { setShowInviteFriends(v => !v); setSelectedFriendIds([]); }}>
                + Invite friends
              </button>
            )}

            {activity.start_time && (isCreator || myRsvp?.response_type === 'im_in') && (
              <div className="add-to-cal-wrap" ref={calRef}>
                <button className="btn-tertiary btn-small" onClick={() => setCalOpen(!calOpen)}>+ Add to Calendar</button>
                {calOpen && (
                  <div className="cal-dropdown">
                    <a className="cal-option" href={getGoogleCalendarUrl(activity)} target="_blank" rel="noopener noreferrer" onClick={() => setCalOpen(false)}>
                      Google Calendar
                    </a>
                    <button className="cal-option" onClick={() => { generateICS(activity); setCalOpen(false); }}>
                      Apple Calendar (.ics)
                    </button>
                  </div>
                )}
              </div>
            )}
            {isCreator && !activity.is_cancelled && (
              <>
                <button
                  className="btn-tertiary btn-small"
                  onClick={() => navigate(`/activity/${id}/edit`)}
                >Edit</button>
                <button className="btn-danger-outline btn-small" onClick={handleCancel} disabled={cancelling}>
                  {cancelling ? 'Cancelling…' : 'Cancel activity'}
                </button>
              </>
            )}
          </div>
          {showInviteFriends && (
            <div className="invite-friends-panel">
              <p className="invite-friends-label">Select friends to invite</p>
              <div className="invite-friends-chips">
                {friends.map(f => (
                  <button
                    key={f.id}
                    type="button"
                    className={`ca-friend-chip${selectedFriendIds.includes(f.id) ? ' selected' : ''}`}
                    onClick={() => toggleFriend(f.id)}
                  >
                    {f.name}
                  </button>
                ))}
              </div>
              {selectedFriendIds.length > 0 && (
                <button
                  className="btn-primary btn-small invite-friends-send"
                  onClick={handleInviteFriends}
                  disabled={inviting}
                >
                  {inviting ? 'Sending…' : `Send invite${selectedFriendIds.length > 1 ? 's' : ''}`}
                </button>
              )}
            </div>
          )}
          {inviteToast && <Toast message={inviteToast} onDismiss={() => setInviteToast(null)} />}
        </div>

        {/* ── Right panel: chat ── */}
        <div className="detail-right">
          <div className="chat-panel">
            <div className="chat-panel-header">
              <div>
                <h2 className="chat-panel-title">Group Chat</h2>
                <span className="chat-panel-sub">{going.length + maybe.length} {going.length + maybe.length === 1 ? 'member' : 'members'}</span>
              </div>
            </div>

            {canChat ? (
              <>
                <div className="chat-messages">
                  {messages.length === 0 ? (
                    <p className="chat-empty">No messages yet. Say something!</p>
                  ) : (
                    messages.map((msg) => (
                      <div key={msg.id} className={`chat-message ${msg.user_id === currentUserId ? 'own' : ''}`}>
                        {msg.user_id !== currentUserId && (
                          <div className="message-meta">
                            <span className="message-author">{msg.display_name || 'Someone'}</span>
                            <span className="message-time">{formatTime(msg.created_at)}</span>
                          </div>
                        )}
                        {msg.user_id === currentUserId && (
                          <div className="message-meta own-meta">
                            <span className="message-time">{formatTime(msg.created_at)}</span>
                            <span className="message-author">Me</span>
                          </div>
                        )}
                        <div className="message-bubble-row">
                          <div className="message-bubble">{msg.content}</div>
                          {msg.user_id === currentUserId && (
                            <button className="msg-delete-icon" onClick={() => deleteMessage(msg.id)} title="Delete">
                              <Trash2 size={12} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>
                <form className="chat-input" onSubmit={handleSendMessage}>
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message…"
                    disabled={!connected}
                  />
                  <button type="submit" className="chat-send-btn" disabled={!connected || !newMessage.trim()}>
                    <Send size={16} strokeWidth={2.5} />
                  </button>
                </form>
              </>
            ) : (
              <div className="chat-locked">
                <p>RSVP "I'm in" or "Maybe" to join the chat.</p>
              </div>
            )}
          </div>
        </div>

      </div>
    </Layout>
  );
}
